"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import EquipmentSelector from "./equipment-selector";
import ImageUpload from "../shared/image-upload";
import MultilangEditor from "../shared/multilang-editor";

interface StudioFormData {
  name: string; // Kept for backward compatibility but not used in UI
  // "on_site" = a fixed physical studio/room; "on_road" = a studio used for road
  // shows. Persisted to the studios.type column. Kits are a SEPARATE entity
  // (see /kits), not represented here.
  type: "on_site" | "on_road";
  capacity: number;
  status: string;
  equipment: Record<string, boolean>;
  infos: Array<{
    lang: string;
    name?: string;
    information: string;
    features?: Record<string, any>;
  }>;
  media: Array<{
    url: string;
    type: "image" | "video" | "360";
    is_primary: boolean;
  }>;
}

interface StudioFormProps {
  studio?: any;
  venueId: number;
  mode: "create" | "edit";
  isBookingAdmin?: boolean;
  // Dialog mode props (optional)
  isOpen?: boolean;
  onClose?: () => void;
  onSubmit?: (data: StudioFormData) => Promise<void>;
}

export default function StudioForm({
  studio,
  venueId,
  mode,
  isBookingAdmin = false,
  isOpen,
  onClose,
  onSubmit,
}: StudioFormProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDialogMode = isOpen !== undefined;

  const [formData, setFormData] = useState<StudioFormData>({
    name: "", // Kept for backward compatibility but not used in UI
    type: "on_site",
    capacity: 6,
    status: "active",
    equipment: {},
    infos: [{ lang: "en", name: "", information: "", features: {} }],
    media: [],
  });

  useEffect(() => {
    if (studio && mode === "edit") {
      setFormData({
        name: studio.name || "",
        type: studio.type === "on_road" ? "on_road" : "on_site",
        capacity: studio.capacity || 6,
        status: studio.statuses?.name || studio.status || "active",
        equipment: studio.equipment || {},
        infos: studio.infos || [{ lang: "en", information: "", features: {} }],
        media: studio.studio_medias || [],
      });
    }
  }, [studio, mode]);

  const handleSubmit = async () => {
    setError(null);

    // Validation
    const primaryInfo =
      formData.infos.find((info) => info.lang === "en") || formData.infos[0];
    if (!primaryInfo || !primaryInfo.name?.trim()) {
      setError("Studio name is required in at least one language");
      return;
    }

    if (formData.capacity < 1) {
      setError("Capacity must be at least 1");
      return;
    }

    if (formData.infos.length === 0 || !formData.infos[0].information.trim()) {
      setError("At least one language description is required");
      return;
    }

    setIsLoading(true);
    try {
      if (isDialogMode && onSubmit) {
        // Dialog mode - use the parent's onSubmit handler
        await onSubmit(formData);
      } else {
        // Page mode - call REST API
        // Get the primary name for the studio name field
        const primaryName =
          formData.infos.find((info) => info.lang === "en")?.name ||
          formData.infos[0]?.name ||
          "";

        // Convert equipment from Record<string, boolean> to string[]
        const apiData = {
          ...formData,
          name: primaryName, // Use the primary language name
          equipment: Object.entries(formData.equipment)
            .filter(([_, enabled]) => enabled)
            .map(([key]) => key),
        };

        const url =
          mode === "create"
            ? `/api/venues/${venueId}/studios`
            : `/api/venues/${venueId}/studios/${studio?.id}`;

        const method = mode === "create" ? "POST" : "PUT";

        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(apiData),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || "Failed to save studio");
        }

        // Non-dialog mode - refresh the page to show updated data
        const successMessage =
          mode === "create"
            ? "Studio created successfully"
            : "Studio updated successfully";
        toast.success(successMessage);

        if (mode === "create") {
          // For create mode, redirect to the new studio's page
          router.push(
            `/superadmin/venue/${venueId}/studios/${result.studio.id}`,
          );
        } else {
          // For edit mode, just refresh to show updated data
          router.refresh();
          setError(null);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to save studio");
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (updates: Partial<StudioFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleEquipmentChange = (equipment: Record<string, boolean>) => {
    updateFormData({ equipment });
  };

  const handleInfosChange = (infos: StudioFormData["infos"]) => {
    updateFormData({ infos });
  };

  const handleMediaChange = (media: StudioFormData["media"]) => {
    updateFormData({ media });
  };

  const formContent = (
    <div className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Basic Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
          <CardDescription>
            Configure the fundamental details of your studio including name and
            descriptions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Type Section — on-site studio vs. on-road studio (used for road shows) */}
          <div className="space-y-2">
            <Label htmlFor="type">Type</Label>
            <Select
              value={formData.type}
              onValueChange={(value) =>
                updateFormData({ type: value as StudioFormData["type"] })
              }
              disabled={isLoading || isBookingAdmin}
            >
              <SelectTrigger id="type" className="md:w-1/2">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="on_site">On-site</SelectItem>
                <SelectItem value="on_road">On the road</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              "On the road" studios are used for road shows. Road-show equipment
              kits are managed separately under Kits.
            </p>
          </div>

          {/* Name & Descriptions Section */}
          <div className="space-y-4">
            <MultilangEditor
              infos={formData.infos}
              onChange={handleInfosChange}
              showNames={true}
              nameLabel="Studio Name"
              descriptionLabel="Description"
            />
          </div>

          {/* Capacity and Status Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="capacity">
                Capacity <span className="text-red-500">*</span>
              </Label>
              <Input
                id="capacity"
                type="number"
                min="1"
                value={formData.capacity}
                onChange={(e) =>
                  updateFormData({ capacity: parseInt(e.target.value) || 1 })
                }
                placeholder="Maximum participants"
                disabled={isLoading || isBookingAdmin}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => updateFormData({ status: value })}
                disabled={isLoading || isBookingAdmin}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Equipment Card */}
      <Card>
        <CardHeader>
          <CardTitle>Equipment & Amenities</CardTitle>
          <CardDescription>
            Select the equipment and features available in this studio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EquipmentSelector
            selected={formData.equipment}
            onChange={handleEquipmentChange}
          />
        </CardContent>
      </Card>

      {/* Media Card */}
      <Card>
        <CardHeader>
          <CardTitle>Media Gallery</CardTitle>
          <CardDescription>
            Upload images and videos to showcase your studio
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ImageUpload
            media={formData.media}
            onChange={handleMediaChange}
            maxFiles={10}
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <Button
          variant="outline"
          onClick={() => {
            if (isDialogMode) {
              onClose?.();
            } else {
              router.push(`/superadmin/venue/${venueId}/studios`);
            }
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>Saving...</>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {mode === "create" ? "Create Studio" : "Update Studio"}
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Return dialog or standalone form based on mode
  if (isDialogMode) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Create New Studio" : `Edit ${studio?.name}`}
            </DialogTitle>
            <DialogDescription>
              Configure the studio details, capacity, equipment, and
              multi-language descriptions.
            </DialogDescription>
          </DialogHeader>
          {formContent}
        </DialogContent>
      </Dialog>
    );
  }

  return formContent;
}
